import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useRouter} from 'next/router';
import {Flex, Text, Center, Divider} from '@chakra-ui/layout';
import {Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter} from '@chakra-ui/modal';
import {CloseIcon} from '@chakra-ui/icons';

import {useTranslate} from '@eksab/i18n';
import {CoinsIcon} from '@eksab/assets/icons/CoinsIcon';
import {getLanguage} from '@eksab/util';

import {ChallengeCard} from '@eksab/features/trivia/components/ChallengeCard';
import {useTriviaStore} from '@eksab/features/trivia/state';
import type {Challenge} from '@eksab/features/trivia/types';

import {JoinCTA} from './JoinCTA';
import {ChooseLanguage} from './ChooseLanguage';
import {CurrentCompetitors} from './CurrentCompetitors';

interface JoinModalProps {
  challenge: Challenge;
  onClose: () => void;
}

export const JoinModal = ({challenge, onClose}: JoinModalProps) => {
  const {locale} = useRouter();
  const format = useIntl().formatNumber;
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const appLanguage = getLanguage(locale);
    const isAppLanguageAvailable = challenge.available_languages.find((lang) => lang === appLanguage);

    if (isAppLanguageAvailable) return appLanguage;

    return challenge.available_languages[0];
  });

  const t = useTranslate();
  const resetTriviaStore = useTriviaStore((state) => state.reset);

  const onJoinChallenge = () => {
    const state = useTriviaStore.getState();

    resetTriviaStore();
    useTriviaStore.setState({
      language: selectedLanguage,
      prevAppLanguage: state.language ? state.prevAppLanguage! : getLanguage(locale),
    });
  };

  return (
    <Modal closeOnOverlayClick={false} size="sm" variant="full" onClose={onClose} isOpen>
      <ModalOverlay />

      <ModalContent px="3" py="4" rounded="3xl" maxH="90vh" overflow="auto">
        <ModalHeader p="0" display="flex" flexDir="column" alignItems="center" gap="3" textAlign="center">
          <CurrentCompetitors challengeID={challenge.id} />

          <Text fontSize="xs" fontWeight="600" color="#8f8f8f" px="6">
            {t(`trivia.join.${challenge.topic}.description`)}
          </Text>

          <Divider w="4" borderColor="#8f8f8f" opacity="1" />
        </ModalHeader>

        <ModalBody px="0" pt="1.125rem" pb="8" display="flex" flexDir="column" gap="3">
          <ChallengeCard challenge={challenge} previewOnly mb="1.5" />

          <Flex
            alignSelf="center"
            align="center"
            gap="1.5"
            px="3.5"
            py="2"
            bg="#e9fff4"
            borderWidth="1px"
            borderColor="#1ba482"
            rounded="2rem"
            color="#8f8f8f"
            fontSize="xs"
            fontWeight="700"
          >
            <CoinsIcon h="8" me="1" />

            <Text fontSize="lg" fontWeight="800" color="#2a2e3a">
              {challenge.entry_fee ? format(challenge.entry_fee) : t('trivia.challenge.no-fees')}
            </Text>

            {!!challenge.entry_fee && t('competition.overview.fee')}
          </Flex>

          {!!challenge.entry_fee && (
            <Text fontSize="xs" fontWeight="600" color="#6c6c6c" textAlign="center" px="3">
              {t('trivia.join.deduction-notice', {fee: format(challenge.entry_fee)})}
            </Text>
          )}

          <ChooseLanguage
            languages={challenge.available_languages}
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
          />
        </ModalBody>

        <ModalFooter p="0" gap="10" justifyContent="space-between">
          <Center as="button" cursor="pointer" rounded="full" boxSize="12" bg="#131313" onClick={() => onClose()}>
            <CloseIcon color="white" />
          </Center>

          <JoinCTA challenge={challenge} onClick={onJoinChallenge} />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
